import { c as _c } from "react/compiler-runtime";
import { Button, CaptureEscKeydown } from '@onefootprint/ui';
import { useIsMutating } from '@tanstack/react-query';
import { useNodesInitialized, useReactFlow } from '@xyflow/react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useMobileLayout from '@/hooks/use-mobile-layout';
import useConfirmClose from '../../hooks/use-confirm-close';
import useStore from '../../hooks/use-store';
import { panelWidthOf, useNodePanel } from './node-panel-context';
import { focusViewportForNode, panelViewport } from './node-panel-pan';

// Shared by the pan + the width animation (matches the agent builder's RightSidebar) so they move as one.
const ANIM_MS = 200;
const ANIM_EASE = [0.4, 0, 0.2, 1] as const;

// The single inline node-config panel: a flex sibling of the canvas. The active node portals its body
// (and optional custom footer) into the slots below; this owns the shell, sizing, and default footer.
const NodePanel = () => {
  const $ = _c(52);
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    descriptor,
    setBodySlot,
    setFooterSlot
  } = useNodePanel();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationKey: ["patchOrgPlaybooksEditSessionsByEditSessionId"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isMutationPending = useIsMutating(t0) > 0;
  const t1 = descriptor?.isDirty ?? false;
  let t2;
  if ($[1] !== descriptor) {
    t2 = () => descriptor?.onClose();
    $[1] = descriptor;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = descriptor?.nodeId;
  let t4;
  if ($[3] !== t3) {
    t4 = [t3];
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = {
      handler: t2,
      deps: t4
    };
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== descriptor) {
    t6 = () => descriptor?.clearDirty();
    $[8] = descriptor;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== isMutationPending || $[11] !== t1 || $[12] !== t5 || $[13] !== t6) {
    t7 = {
      isDirty: t1,
      isMutationPending,
      onCloseParent: t5,
      onClearDirty: t6
    };
    $[10] = isMutationPending;
    $[11] = t1;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const {
    attemptClose
  } = useConfirmClose(t7);
  let t8;
  if ($[15] !== descriptor) {
    t8 = panelWidthOf(descriptor);
    $[15] = descriptor;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const width = t8;
  const shouldReduceMotion = useReducedMotion();
  const isMobile = useMobileLayout();
  const reactFlow = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const activeNodeId = descriptor?.nodeId;
  let t9;
  if ($[17] !== activeNodeId || $[18] !== reactFlow) {
    t9 = activeNodeId ? reactFlow.getNode(activeNodeId)?.measured?.width ?? 0 : 0;
    $[17] = activeNodeId;
    $[18] = reactFlow;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const activeNodeWidth = t9;
  const prevWidthRef = useRef(0);
  const pannedForRef = useRef(null);
  const anchorless = descriptor?.anchorless ?? false;
  const {
    data: t10,
    clearNodeFocus
  } = useStore();
  const {
    focusNodeId
  } = t10;
  let t11;
  if ($[20] !== activeNodeId || $[21] !== activeNodeWidth || $[22] !== anchorless || $[23] !== clearNodeFocus || $[24] !== focusNodeId || $[25] !== isMobile || $[26] !== nodesInitialized || $[27] !== reactFlow || $[28] !== shouldReduceMotion || $[29] !== width) {
    t11 = () => {
      if (!activeNodeId) {
        prevWidthRef.current = 0;
        pannedForRef.current = null;
        return;
      }
      if (!nodesInitialized) {
        return;
      }
      const isFocusing = !!focusNodeId && focusNodeId === activeNodeId;
      if (!isFocusing && pannedForRef.current === activeNodeId) {
        return;
      }
      if (!anchorless && activeNodeWidth <= 0) {
        return;
      }
      if (isFocusing) {
        clearNodeFocus();
      }
      const target = isFocusing ? focusViewportForNode(reactFlow, {
        nodeId: activeNodeId,
        panelWidth: isMobile ? 0 : width
      }) : panelViewport(reactFlow, {
        anchorless,
        isMobile,
        nodeId: activeNodeId,
        panelWidth: width,
        prevPanelWidth: prevWidthRef.current
      });
      prevWidthRef.current = width;
      pannedForRef.current = activeNodeId;
      if (!target) {
        return;
      }
      reactFlow.setViewport(target, {
        duration: shouldReduceMotion ? 0 : ANIM_MS
      });
    };
    $[20] = activeNodeId;
    $[21] = activeNodeWidth;
    $[22] = anchorless;
    $[23] = clearNodeFocus;
    $[24] = focusNodeId;
    $[25] = isMobile;
    $[26] = nodesInitialized;
    $[27] = reactFlow;
    $[28] = shouldReduceMotion;
    $[29] = width;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== activeNodeId || $[32] !== activeNodeWidth || $[33] !== anchorless || $[34] !== focusNodeId || $[35] !== isMobile || $[36] !== nodesInitialized || $[37] !== reactFlow || $[38] !== shouldReduceMotion || $[39] !== width) {
    t12 = [activeNodeId, activeNodeWidth, width, nodesInitialized, anchorless, focusNodeId, reactFlow, shouldReduceMotion, isMobile];
    $[31] = activeNodeId;
    $[32] = activeNodeWidth;
    $[33] = anchorless;
    $[34] = focusNodeId;
    $[35] = isMobile;
    $[36] = nodesInitialized;
    $[37] = reactFlow;
    $[38] = shouldReduceMotion;
    $[39] = width;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  useEffect(t11, t12);
  let t13;
  if ($[41] !== attemptClose || $[42] !== descriptor || $[43] !== isMutationPending || $[44] !== setBodySlot || $[45] !== setFooterSlot || $[46] !== shouldReduceMotion || $[47] !== t || $[48] !== width) {
    t13 = descriptor && <motion.aside initial={shouldReduceMotion ? false : {
      width: 0
    }} animate={{
      width
    }} exit={{
      width: 0
    }} transition={shouldReduceMotion ? {
      duration: 0
    } : {
      duration: ANIM_MS / 1000,
      ease: ANIM_EASE
    }} data-node-panel={true} className="relative max-md:absolute max-md:inset-y-0 max-md:right-0 max-md:z-10 max-md:max-w-full shrink-0 overflow-hidden"><div role="dialog" aria-label="Node configuration" className="absolute right-0 top-0 bottom-0 flex flex-col max-md:max-w-full border-l border-tertiary bg-primary" style={{
        width
      }}><CaptureEscKeydown id="playbook-node-panel" onEscKeydown={attemptClose} /><div ref={setBodySlot} className="node-panel-body flex-1 overflow-y-auto" /><footer className="flex items-center justify-between w-full shrink-0 border-t border-tertiary border-solid px-3 py-3 max-md:pb-[max(0.75rem,env(safe-area-inset-bottom))]">{descriptor.hasCustomFooter ? <div ref={setFooterSlot} className="contents" /> : <><div className="flex pl-2"><Button variant="secondary" onClick={attemptClose.handler}>{t(_temp)}</Button></div>{descriptor.form && descriptor.canEdit ? <div className="flex flex-row gap-3 ml-auto"><Button form={descriptor.form} loading={isMutationPending} disabled={isMutationPending || descriptor.isSaveBlocked || !(descriptor.isDirty || descriptor.isNew)} type="submit" variant="primary">{t(_temp2)}</Button></div> : null}</>}</footer></div></motion.aside>;
    $[41] = attemptClose;
    $[42] = descriptor;
    $[43] = isMutationPending;
    $[44] = setBodySlot;
    $[45] = setFooterSlot;
    $[46] = shouldReduceMotion;
    $[47] = t;
    $[48] = width;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== t13) {
    t14 = <AnimatePresence>{t13}</AnimatePresence>;
    $[50] = t13;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  return t14;
};
export default NodePanel;
function _temp($) {
  return $.navigation.close;
}
function _temp2($_0) {
  return $_0.navigation.save;
}